
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { Database } from "@/integrations/supabase/types";

type DbProfile = Database["public"]["Tables"]["profiles"]["Row"];

interface UseProfileDataProps {
  username?: string;
  isPublic?: boolean;
}

interface ProfileData {
  loading: boolean;
  error: string | null;
  profile: (DbProfile & { email?: string }) | null;
  listings: any[];
}

export const useProfileData = ({ username, isPublic = false }: UseProfileDataProps): ProfileData => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<(DbProfile & { email?: string }) | null>(null);
  const [listings, setListings] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadProfileAndListings = async () => {
      try {
        let userId: string | undefined;
        
        if (isPublic) {
          if (!username) {
            setError("Profile not found");
            return;
          }

          const { data: profileData, error: profileError } = await supabase
            .from("profiles")
            .select("*")
            .ilike("username", username)
            .maybeSingle();

          if (profileError) throw new Error(`Error fetching profile: ${profileError.message}`);
          if (!profileData) throw new Error(`Profile not found for username: ${username}`);

          userId = profileData.id;
          setProfile(profileData);
        } else {
          const { data: { session }, error: sessionError } = await supabase.auth.getSession();
          
          if (sessionError) throw new Error(`Session error: ${sessionError.message}`);
          if (!session) {
            navigate("/auth");
            return;
          }
          
          const { data: profileData, error: profileError } = await supabase
            .from("profiles")
            .select("*")
            .eq("id", session.user.id)
            .maybeSingle();

          if (profileError) throw new Error(`Error fetching profile: ${profileError.message}`);
          if (!profileData) {
            navigate("/edit-profile");
            return;
          }

          setProfile({ ...profileData, email: session.user.email });
          userId = session.user.id;
        }

        if (userId) {
          const { data: listingsData, error: listingsError } = await supabase
            .from("listings")
            .select("*")
            .eq("user_id", userId)
            .order("created_at", { ascending: false });

          if (listingsError) throw new Error(`Error fetching listings: ${listingsError.message}`);
          
          const transformedListings = listingsData?.map(listing => ({
            ...listing,
            images: Array.isArray(listing.images) 
              ? listing.images.map(img => String(img))
              : []
          })) || [];

          setListings(transformedListings);
        }
      } catch (error) {
        console.error("Error in MySpace component:", error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
        toast.error(errorMessage);
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    loadProfileAndListings();
  }, [navigate, isPublic, username]);

  return { loading, error, profile, listings };
};
